/**
 * Per-account namespacing for persisted client state.
 *
 * The leak this closes: dock pins, desktop tiles, panel widgets, widget
 * settings and the cached permission list all lived at fixed localStorage keys
 * and were never cleared on an account change. `setToken(null)` dropped the open
 * windows and nothing else, so an admin's desktop — icons for Users, Analytics,
 * App Manager — survived into a guest session in the same browser, and the guest
 * booted holding the admin's cached `arclog.perms` until the next fetch landed.
 *
 * Namespacing rather than clearing is deliberate: a user who signs out and back
 * in gets their own layout back, which clearing would destroy. Each account gets
 * its own key space; switching accounts simply reads a different one.
 *
 * None of this is a security boundary. localStorage is user-writable, so the
 * server-filtered package list is what decides what may be seen — see
 * apps/server/src/packages/visibility.mjs, and the render-path filtering in
 * store.ts that drops any tile referencing a package the server did not serve.
 * This file only stops one account's *preferences* being shown to another.
 */

/** Keys that describe one account's desktop and must never cross accounts. */
export const SCOPED_KEYS = [
  "arclog.dockPins",
  "arclog.desktop.items",
  "arclog.panel.items",
  "arclog.widget.settings",
  "arclog.perms",
];

/** Scope used when nobody is signed in. Never migrated into. */
export const ANON_SCOPE = "anon";

function decodeSegment(seg) {
  const b64 = String(seg).replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  if (typeof atob === "function") return atob(pad);
  return Buffer.from(pad, "base64").toString("binary");
}

/**
 * Which account a token belongs to, as a key-safe string.
 *
 * The JWT is read for its *identity*, not trusted for anything: a forged token
 * only gets you your own separate pile of dock pins. Falls back through
 * username → sub → role so a token shape change degrades to a coarser scope
 * rather than to a shared one.
 *
 * @param {string | null | undefined} token
 * @returns {string}
 */
export function scopeIdFromToken(token) {
  if (!token || typeof token !== "string") return ANON_SCOPE;
  const parts = token.split(".");
  if (parts.length < 2) return ANON_SCOPE;
  try {
    const payload = JSON.parse(decodeSegment(parts[1]));
    const who = payload.username || payload.sub || payload.userId || payload.role;
    if (!who) return ANON_SCOPE;
    // Restrict to characters that cannot collide with the "::" separator or
    // produce two spellings of one account.
    return String(who).toLowerCase().replace(/[^a-z0-9._-]+/g, "_");
  } catch {
    return ANON_SCOPE;
  }
}

/**
 * @param {string} baseKey
 * @param {string} scopeId
 * @returns {string}
 */
export function scopedKey(baseKey, scopeId) {
  return `${baseKey}::${scopeId || ANON_SCOPE}`;
}

/**
 * Move pre-namespacing state into the first real account that signs in, once.
 *
 * Without this every existing install would silently boot to an empty desktop
 * after the upgrade. The anon scope is skipped on purpose: a signed-out visitor
 * must not be able to claim the owner's layout by loading the page.
 *
 * @param {Storage | { getItem(k: string): string | null, setItem(k: string, v: string): void, removeItem(k: string): void }} storage
 * @param {string} scopeId
 * @param {string[]} [keys]
 * @returns {string[]} base keys that were migrated
 */
export function migrateLegacyKeys(storage, scopeId, keys = SCOPED_KEYS) {
  if (!storage || !scopeId || scopeId === ANON_SCOPE) return [];
  const moved = [];
  for (const base of keys) {
    try {
      const legacy = storage.getItem(base);
      if (legacy == null) continue;
      // An account that already has scoped state keeps it; the legacy value is
      // stale by definition and is dropped rather than allowed to linger and
      // migrate into some *other* account later.
      if (storage.getItem(scopedKey(base, scopeId)) == null) {
        storage.setItem(scopedKey(base, scopeId), legacy);
        moved.push(base);
      }
      storage.removeItem(base);
    } catch {
      /* a full or disabled storage must not stop the OS booting */
    }
  }
  return moved;
}

/**
 * The storage namespace for one app's `ctx.appState`, SCOPED BY ACCOUNT.
 *
 * The leak this closes: `ctx.appState` used to key by app id alone
 * (`arclog.appstate.<appId>.<key>`), so on a shared browser one account's
 * app data — Trading's audit ring, a document's last-open path, anything an
 * app persists — was read back by the NEXT account that opened the same app.
 * Same shape as the desktop keys above: one namespace per (app, account)
 * pair; switching accounts re-points, never clears.
 *
 * Like everything in this file this is a PRIVACY boundary, not a security
 * one — localStorage is user-writable. Sensitive per-user data belongs in
 * `ctx.db` (server-enforced per-account); secrets in `ctx.keys`. See
 * docs/sdk/24-security.md.
 *
 * @param {string} appId
 * @param {string} scopeId
 * @returns {string} `arclog.appstate.<appId>::<scope>`
 */
export function appStateNamespace(appId, scopeId) {
  return scopedKey(`arclog.appstate.${appId}`, scopeId || ANON_SCOPE);
}

/**
 * Move UNSCOPED appState keys (`arclog.appstate.<appId>.<k>`) into the
 * account-scoped namespace, once, for the first real account to open the app
 * after the upgrade. Legacy keys are removed either way, so they can never
 * surface under some *other* account later. Anon scope is skipped: a
 * signed-out visitor must not claim them.
 *
 * Keys already scoped under a different account (`::<other>.<k>`) are
 * untouched — the `.` prefix check cannot match them (a scope separator is
 * `::`, never `.`), so two accounts' namespaces never collide during a scan.
 *
 * @param {Storage | { getItem(k: string): string | null, setItem(k: string, v: string): void, removeItem(k: string): void, key?(i: number): string | null, length?: number, keys?(): string[] }} storage
 * @param {string} appId
 * @param {string} scopeId
 * @returns {string[]} full legacy keys that were migrated
 */
export function migrateLegacyAppStateKeys(storage, appId, scopeId) {
  if (!storage || !appId || !scopeId || scopeId === ANON_SCOPE) return [];
  const legacyPrefix = `arclog.appstate.${appId}.`;
  const ns = appStateNamespace(appId, scopeId);
  let allKeys;
  try {
    allKeys =
      typeof storage.keys === "function"
        ? storage.keys()
        : Array.from({ length: storage.length || 0 }, (_, i) => storage.key(i));
  } catch {
    return [];
  }
  const moved = [];
  for (const k of allKeys) {
    if (typeof k !== "string" || !k.startsWith(legacyPrefix)) continue;
    try {
      const value = storage.getItem(k);
      if (value == null) continue;
      // `<ns>.<key>` — the suffix after the legacy prefix is the app's key.
      const target = `${ns}.${k.slice(legacyPrefix.length)}`;
      if (storage.getItem(target) == null) {
        storage.setItem(target, value);
        moved.push(k);
      }
      storage.removeItem(k);
    } catch {
      /* one bad key must not break the app's boot */
    }
  }
  return moved;
}
